
import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { SelectOptions } from '../../interfaces/select-options';
import { ConnectServerService } from '../../../../services/connect-server.service';
import { CheckFormValidityService } from '../../../../services/check-form-validity.service';
import { PopupDialogService } from '../../../../services/popup-dialog.service';
import { Connect } from '../../../../classes/connect';
import { ApiResponse } from '../../../../interfaces/api-response';
import { SelectTechnicalData } from '../../interfaces/select-technical-data';

@Component({
    selector: 'app-technical-data',
    imports: [
    MatExpansionModule,
    ReactiveFormsModule
],
    templateUrl: './technical-data.component.html',
    styleUrl: './technical-data.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TechnicalDataComponent {

  readonly panelOpenState = signal(false);

  @Input() idarticle: number = 0;
  @Input() listTechnicalData: SelectTechnicalData | null = null;

  saved: boolean = false;
  submitted = false;
  technicalDataForm: FormGroup;

  constructor(private fb: FormBuilder, private connectServerService: ConnectServerService,
    private popupDialogService: PopupDialogService, private checkFormValidityService: CheckFormValidityService) {
    this.technicalDataForm = this.fb.group({
      //techniques: this.fb.array([this.createTechnique()]),
      technique: [null, [Validators.maxLength(250)]],
      measures: this.fb.array([this.createMeasures()])
    })
  }

  ngOnInit(): void {
    this.getTechnicalDataInfo();
  }

  // Funzione che crea un singolo gruppo di campi
  // createTechnique(): FormGroup {
  //   return this.fb.group({
  //     id: [0],
  //     technique: [null, [Validators.required]]
  //   });
  // }

  createMeasures(): FormGroup {
    return this.fb.group({
      id: [0],
      unit: [null, Validators.required],
      height: [null, [Validators.maxLength(6)]],
      width: [null, [Validators.maxLength(6)]],
      depth: [null, [Validators.maxLength(6)]],
      diameter: [null, [Validators.maxLength(6)]],
      length: [null, [Validators.maxLength(6)]],
      thickness: [null, [Validators.maxLength(6)]],
      weight: [null, [Validators.maxLength(6)]],
      others: this.fb.array([this.createOthers()]),
      missing: [null],
      validity: [null],
    });
  }

  createOthers(): FormGroup {
    return this.fb.group({
      id: [0],
      other: [null, [Validators.maxLength(250)]]
    });
  }

  // Restituisce il FormArray per una gestione più semplice
  // get techniques(): FormArray {
  //   return this.technicalDataForm.get('techniques') as FormArray;
  // }

  get measures(): FormArray {
    return this.technicalDataForm.get('measures') as FormArray;
  }

  // Aggiunge un nuovo gruppo di campi al FormArray
  // addTechnique() {
  //   this.techniques.push(this.createTechnique());
  // }

  addMeasures() {
    this.measures.push(this.createMeasures());
  }

  addOthers(index: number) {
    const otherArray = this.measures.at(index).get('others') as FormArray;
    otherArray.push(this.createOthers());
  }

  // Rimuove un gruppo di campi dal FormArray, lasciando sempre almeno uno
  // removeTechniques(index: number) {
  //   if (this.techniques.length > 1) {
  //     this.techniques.removeAt(index);
  //   }
  // }

  removeMeasures(index: number) {
    if (this.measures.length > 1) {
      this.measures.removeAt(index);
    }
  }

  removeOthers(index: number, othersIndex: number) {
    const othersArray = this.measures.at(index).get('others') as FormArray;
    if (othersArray.length > 1) {
      othersArray.removeAt(othersIndex);
    }
  }

  // Funzione per ottenere il FormArray 'attributions' per un chronology specifico
  getMeasuresControl(index: number): FormArray {
    return this.measures.at(index).get('others') as FormArray;
  }

  private getTechnicalDataInfo() {
    this.connectServerService.getRequest('technicalDataInfo', { idarticle: this.idarticle })
      .subscribe(
        (val: ApiResponse<{ technicalDataForm: { measures: any[], technique: string } }>) => {
          // console.log('val:', val);
          // Pulisci gli array esistenti
          //this.techniques.clear();
          this.measures.clear();
          // Popola l'array techniques
          // if (val.data.technicalDataForm.techniques && val.data.technicalDataForm.techniques.length > 0) {
          //   val.data.technicalDataForm.techniques.forEach(technique => {
          //     const techniqueGroup = this.createTechnique();
          //     // console.log('technique:', technique);
          //     techniqueGroup.patchValue(technique);
          //     this.techniques.push(techniqueGroup);
          //   });
          // } else {
          //   // Se non ci sono dati, mantieni almeno un elemento vuoto
          //   this.techniques.push(this.createTechnique());
          // }
          this.technicalDataForm.patchValue({
            technique: val.data.technicalDataForm.technique
          });
          // Popola l'array measures
          if (val.data.technicalDataForm.measures && val.data.technicalDataForm.measures.length > 0) {
            val.data.technicalDataForm.measures.forEach(measure => {
              const measureGroup = this.createMeasures();
              measureGroup.patchValue(measure);

              // Gestisci l'array 'others' se presente
              if (measure.others && measure.others.length > 0) {
                const othersArray = measureGroup.get('others') as FormArray;
                othersArray.clear();
                measure.others.forEach((other: any) => {
                  const otherGroup = this.createOthers();
                  otherGroup.patchValue(other);
                  othersArray.push(otherGroup);
                });
              }

              this.measures.push(measureGroup);
            });
          } else {
            // Se non ci sono dati, mantieni almeno un elemento vuoto
            this.measures.push(this.createMeasures());
          }

          // Applica i valori del form principale se presenti
          // if (val.data.technicalDataForm) {
          //   this.technicalDataForm.patchValue(val.data.technicalDataForm);
          // }

          this.saved = this.checkFormValidityService.areAllRequiredFieldsFilled(this.technicalDataForm);
          if (this.saved) {
            this.panelOpenState.set(false);
          }
          else {
            this.panelOpenState.set(true);
          }
        }
      )
  }

  private setTechnicalDataInfo() {
    const formValues = this.technicalDataForm.getRawValue();
    // console.log('val:', formValues);
    this.connectServerService.postRequest('updateTechnicalData',
      { idarticle: this.idarticle, infoTechnical: formValues })
      .subscribe(
        (val: ApiResponse<null>) => {
          this.popupDialogService.popupDialog(val);
          this.getTechnicalDataInfo();
        }
      )
  }

  save() {
    this.submitted = true
    if (this.technicalDataForm.valid) {
      this.setTechnicalDataInfo();
    }
  }

}
